import {
  Cancel,
  CancelOutlined,
  CheckCircle,
  Close,
  Download,
  Send,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Button as MuiButton,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Appbar from "../../components/Appbar";
import { convertDate, convertToINR } from "../../helpers/Utils";
import {
  addInvoice,
  getGRNs,
  updateInvoice,
  uploadInvoiceImage,
} from "../../services/invoiceService";
import styles from "./AddEditInvoice.module.css";
import LoaderV2 from "./components/LoaderV2";
import UploadFile from "./components/UploadFile";
import { useToast } from "./hooks/useToast";
import { COLOR_CONSTANTS } from "../../helpers/Colors";
import InvoicePreview from "./components/InvoicePreview";
import { parseNumericInput, coerceNumericPayload, isInvoiceRequiredFieldsMissing } from "./utils/addEditInvoice";

let initialValues = {
  invoice_no: "",
  invoice_date: "",
  invoice_value: "",
  additional_charges: 0,
  irn_invoice: false,
  invoice_image: "",
  grn_id: null,
  grn_date: null,
  grn_value: null,
  po_code: "",
  bill_to_address: null,
  ship_to_address: null,
  hsn_sac_codes: [],
  invoice_po_result: null,
  parser_invoice_result: null,
  milestone: null,
  invoices: [],
};

const InvoiceField = (props) => {
  const {
    label,
    placeholder,
    helperText,
    value,
    onChange,
    onBlur,
    name,
    type,
    disabled,
    required,
    validation = "IGNORE",
    errorMessage = "",
    customEle = null,
  } = props;
  return (
    <Stack direction={"row"} gap={"24px"} mb={"24px"}>
      <Box
        width={"240px"}
        display={"flex"}
        flexDirection={"row"}
        gap={1}
        alignItems={"center"}
      >
        {validation !== "IGNORE" ? (
          <>
            {validation ? (
              <CheckCircle
                style={{ color: COLOR_CONSTANTS["tertiary-green/variant"] }}
              />
            ) : (
              <Cancel
                style={{ color: COLOR_CONSTANTS["tertiary-red/default"] }}
              />
            )}
          </>
        ) : (
          <Box height={24} width={24} />
        )}
        {label} {required ? "*" : ""}
      </Box>
      {customEle ? (
        <div style={{ width: "360px" }}>{customEle}</div>
      ) : (
        <div>
          {type === "textarea" ? (
            <textarea
              placeholder={placeholder}
              className={`${styles.invoiceTextArea} ${validation === false ? styles.errorOutline : ""
                }`}
              value={value}
              onChange={onChange}
              name={name}
              disabled={disabled}
            />
          ) : (
            <input
              placeholder={placeholder}
              className={`${styles.invoiceTextField} ${validation === false ? styles.errorOutline : ""
                }`}
              value={value}
              onChange={onChange}
              name={name}
              type={type ?? "text"}
              onBlur={onBlur}
              onFocus={type === "number" ? (e) => e.target.select() : undefined}
              disabled={disabled}
            />
          )}
          {helperText ? (
            <div className={styles.helperText}>{helperText}</div>
          ) : null}
          {validation !== "IGNORE" && !validation && errorMessage ? (
            <div
              className={styles.helperText}
              style={{
                color: COLOR_CONSTANTS["tertiary-red/default"],
                marginTop: "2px",
              }}
            >
              {errorMessage}
            </div>
          ) : null}
        </div>
      )}
    </Stack>
  );
};

function getInitialValues(state) {
  if (!state || !state.edit)
    return {
      ...initialValues,
      amount: state.amount || null,
      po_code: state.po_code || null,
      milestone: state.milestone || null,
      invoices: state.invoices || [],
    };
  let {
    id,
    invoice_no,
    invoice_date,
    invoice_value,
    additional_charges,
    irn_invoice,
    invoice_image,
    grn_date,
    grn_id,
    grn_value,
    po_code,
    validation_result,
    ship_to_address,
    bill_to_address,
    hsn_sac_codes,
    system_validation_status,
    milestone,
    invoices,
    billing_entity,
    amount
  } = state;

  invoice_date = convertDate(invoice_date, true);
  grn_date = grn_date ? convertDate(grn_date, true) : null;

  return {
    id,
    invoice_no,
    invoice_date,
    invoice_value,
    additional_charges,
    irn_invoice,
    invoice_image,
    grn_date,
    grn_id,
    grn_value,
    po_code,
    validation_result,
    ship_to_address,
    bill_to_address,
    hsn_sac_codes,
    system_validation_status,
    milestone,
    invoices,
    billing_entity,
    amount
  };
}

const MILESTONE_MAPPING = {
  0: "A",
  1: "B",
  2: "C",
  3: "D",
  4: "E",
};

const AddEditInvoice = () => {
  const location = useLocation();
  const naviagate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const initialValues = getInitialValues(location.state);
  const [values, setValues] = useState(initialValues);
  const [validations, setValidations] = useState({});
  const [editedImage, setEditedImage] = useState(null);

  const navigate = useNavigate();

  const po_code = location.state.po_code;
  const source = location.state.source;

  const { isLoading: grnLoading, isFetching: grnFetching, data: grnData } = useQuery(
    ["getGRN", po_code],
    () => getGRNs({ po_code }),
    {
      retry: 2,
      refetchOnWindowFocus: false,
    }
  );

  // Auto-map GRN when exactly one GRN exists and none is already selected.
  // Skip while a refetch is in flight so we don't act on a cached response
  // from a previously-viewed PO.
  React.useEffect(() => {
    if (grnFetching) return;
    if (grnData?.length === 1) {
      const { grn_id, grn_value, grn_date } = grnData[0];
      setValues((prev) => {
        if (prev.grn_id) return prev;
        return {
          ...prev,
          grn_id,
          grn_value,
          grn_date: convertDate(grn_date, true),
        };
      });
    }
  }, [grnData, grnFetching]);

  const { isLoading: isUploading, mutateAsync: uploadInvoice } = useMutation(
    uploadInvoiceImage,
    {
      onError: (error) => {
        showToast({ type: "error", message: "Error uploading image" });
      },
    }
  );

  const _uploadInvoiceCopy = () => {
    if (editedImage) {
      let formData = new FormData();
      formData.append("image", editedImage);
      return uploadInvoice({
        payload: formData,
        invoiceNo: values.invoice_no,
        poCode: po_code,
      });
    }
  };

  const _onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
    showToast({ type: "success", message: "Updated invoice successfully" });
    naviagate(-1);
    return;
  };

  const _onError = (error) => {
    showToast({
      type: "error",
      message: `Error updating invoice: ${error.response.data}`,
    });
    return;
  };

  const { isLoading: isUpdating, mutateAsync: updateInvoiceMutate } =
    useMutation(updateInvoice, {
      onSuccess: async (data) => {
        await _uploadInvoiceCopy();
        _onSuccess();
      },
      onError: (error) => _onError(error),
    });

  const { isLoading: isAdding, mutateAsync: addInvoiceMutate } = useMutation(
    addInvoice,
    {
      onSuccess: async (data) => {
        await _uploadInvoiceCopy();
        _onSuccess();
      },
      onError: (error) => _onError(error),
    }
  );

  const handleInputChange = (e) => {
    let { name, value, type } = e.target;
    if (type === "radio") {
      value = JSON.parse(value);
    } else if (type === "number") {
      value = parseNumericInput(value);
    }
    setValues({
      ...values,
      [name]: value,
    });
  };

  const clearInvoiceImage = () => {
    setValues({
      ...values,
      invoice_image: "",
    });
  };

  if (isUpdating || isUploading || isAdding || grnLoading) return <LoaderV2 />;

  if (!location.state) return <div>No data found</div>;

  const isEditing = location.state.edit;

  const { parser_invoice_result, invoice_po_result } =
    initialValues.validation_result || {};

  const getValidationStatus = (field, checkInvoiceResult = false) => {
    if (!isEditing || values.system_validation_status == null) return "IGNORE";
    if (validations[field] !== undefined) {
      return validations[field];
    }
    if (checkInvoiceResult) {
      return (
        parser_invoice_result?.validation_field_results[field]?.match_status &&
        invoice_po_result?.validation_field_results[field]?.match_status
      );
    }
    return parser_invoice_result?.validation_field_results[field]?.match_status;
  };

  const validateNode = (
    value,
    field,
    checkInvoiceResult = false,
    ignore = false
  ) => {
    // check if the field is edited
    if (values[field] !== initialValues[field]) {
      // If the check to be ignored
      if (ignore) {
        return "IGNORE";
      }

      var parserValue =
        parser_invoice_result?.validation_field_results?.[field]?.parser_value;

      var poValue =
        invoice_po_result?.validation_field_results?.[field]?.po_value;

      // Roundoff `invoice_value` to 2-decimal points
      if (field === "invoice_value") {
        value = value && parseFloat(value).toFixed(2);
        parserValue = parserValue && parseFloat(parserValue).toFixed(2);
        poValue = poValue && parseFloat(poValue).toFixed(2);
        const { INVOICE_VAL_DELTA = 10 } =
          window.config.AUTO_VALIDATION_CONFIG || {};
        if (checkInvoiceResult) {
          // For invoice_val, we are checking if the value is within the delta range
          return parserValue
            ? Math.abs(value - parserValue) <= INVOICE_VAL_DELTA &&
            Math.abs(value - poValue) <= INVOICE_VAL_DELTA
            : Math.abs(value - poValue) <= INVOICE_VAL_DELTA;
        }
      }

      if (checkInvoiceResult) {
        return parserValue
          ? value === parserValue && value === poValue
          : value === poValue;
      }
      return value === parserValue;
    }
  };

  const validationRoot = {
    invoice_no: (value) => validateNode(value, "invoice_no", false, true),
    invoice_date: (value) => validateNode(value, "invoice_date", false, true),
    invoice_value: (value) => validateNode(value, "invoice_value", true),
    po_code: (value) => validateNode(value, "po_code", true),
    hsn_sac_codes: (value) => validateNode(value, "hsn_code", true),
  };

  const validateOnBlur = (e) => {
    if (
      !isEditing ||
      values.system_validation_status == null ||
      !initialValues.validation_result
    )
      return;
    let { name, value } = e.target;
    if (validationRoot.hasOwnProperty(name)) {
      let isValid = validationRoot[name](value);
      // console.log({ isValid });
      name = name === "hsn_sac_codes" ? "hsn_code" : name;
      setValidations({
        ...validations,
        [name]: isValid,
      });
    }
  };

  const getErrorMessage = (field) => {
    const msg1 =
      "Value does not match with the invoice document. Enter manually.";
    const msg2 = "Value does not match with the PO.";

    if (validations[field] !== undefined) {
      return msg2;
    }

    if (parser_invoice_result?.validation_field_results[field]?.match_status) {
      //true
      if (invoice_po_result?.validation_field_results[field]?.match_status) {
        //true
        return null;
      }
      return msg2;
    } else {
      return msg1;
    }
  };

  const isSaveDisabled = () => {
    if (isInvoiceRequiredFieldsMissing(values)) return true;
    if (isEditing && values.system_validation_status === 2) return false;
    return JSON.stringify(initialValues) === JSON.stringify(values);
  };

  const getMilestoneVal = () => {
    let result = "";
    let amount = "";

    if (values.milestone) {
      const milestoneArr = values.milestone.split("-");
      let index = values.invoices.length;
      if (isEditing) {
        // If editing, find the index of the invoice from the invoices array
        const _index = values.invoices.findIndex(
          (invoice) => invoice.invoice_no === values.invoice_no
        );
        index = _index >= 0 ? _index : index;
      }

      if (values.invoices?.length >= 0) {
        const mapping = MILESTONE_MAPPING[index];

        if (isEditing) amount = convertToINR(values?.invoice_value);
        else {
          const pending = values?.amount - values?.invoices.reduce((amount, inv) => amount += inv.invoice_value, 0);
          const currentMilestonePercentage = Number(milestoneArr[index]);
          const pendingMilestonePercentage = milestoneArr.slice(index).reduce((total, milestone) => total += Number(milestone), 0);
          const milestoneAmount = currentMilestonePercentage / pendingMilestonePercentage * pending;
          amount = convertToINR(milestoneAmount);
        }

        result = mapping
          ? `Milestone ${MILESTONE_MAPPING[index]} - ${milestoneArr[index]}% | ${amount}`
          : `${milestoneArr[index]}% | ${amount}`;
      }
    }
    return {
      res: result,
      helper: `${amount ? amount + ': ' : ''}Expected invoice value for this milestone`,
    };
  };

  return (
    <div>
      <Appbar
        title={"Invoice Details"}
        subTitle={location.state.po_code}
        goBack
        navigateTo={"/dashboard/invoice-management/"}
        right={<div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px' }}>Order Value</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '2px' }}>{convertToINR(initialValues.amount)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px' }}>Invoiced Value</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '2px' }}>{convertToINR(values.invoices.reduce((amount, inv) => amount += inv.invoice_value, 0))}</div>
          </div>
        </div>}
      />
      <Box margin={"auto"}>
        <Grid container spacing={"24px"} padding={3} marginBottom={"120px"}>
          {isEditing ? (
            <Grid item xs={6}>
              <div className="ft-16 bold" style={{ marginBottom: "12px" }}>
                Uploaded invoice
              </div>
              <InvoicePreview
                type={values?.invoice_image?.file_name?.split(".").pop()}
                url={values.invoice_image.pre_signed_url}
              />
            </Grid>
          ) : null}
          <Grid item xs={isEditing ? 6 : 12}>
            <div className="ft-16 bold" style={{ marginBottom: "12px" }}>
              Invoice fields
            </div>
            <Box padding={"24px"} bgcolor={"white"} borderRadius={2}>
              {source === "FINS" ? (
                <InvoiceField
                  label="Milestone"
                  placeholder="Enter milestone"
                  value={getMilestoneVal().res}
                  disabled
                  name={"milestone"}
                />
              ) : null}
              <InvoiceField
                label="Invoice Number"
                placeholder="Enter invoice number"
                value={values.invoice_no}
                onChange={handleInputChange}
                name={"invoice_no"}
                onBlur={validateOnBlur}
                validation={getValidationStatus("invoice_no")}
                errorMessage={getErrorMessage("invoice_no")}
                required
              />

              <InvoiceField
                label="Invoice date (YYYY-MM-DD)"
                placeholder="Enter invoice date"
                value={values.invoice_date}
                onChange={handleInputChange}
                name={"invoice_date"}
                type="date"
                onBlur={validateOnBlur}
                validation={getValidationStatus("invoice_date")}
                errorMessage={getErrorMessage("invoice_date")}
                required
              />

              <InvoiceField
                label="Invoice value"
                placeholder="Enter invoice value"
                value={values.invoice_value}
                onChange={handleInputChange}
                helperText={source === 'FINS' && getMilestoneVal().helper}
                name={"invoice_value"}
                type={"number"}
                validation={getValidationStatus("invoice_value", true)}
                errorMessage={getErrorMessage("invoice_value")}
                onBlur={validateOnBlur}
                required
              />

              <InvoiceField
                label="PO Number"
                placeholder="Enter PO Number"
                value={values.po_code}
                onChange={handleInputChange}
                validation={getValidationStatus("po_code", true)}
                name={"po_code"}
                onBlur={validateOnBlur}
                errorMessage={getErrorMessage("po_code")}
              />

              <InvoiceField
                label="Billing Entity"
                placeholder="Enter Billing Entity"
                value={values.billing_entity}
                onChange={handleInputChange}
                validation={getValidationStatus("bill_to_name", true)}
                name={"billing_entity"}
                onBlur={validateOnBlur}
                errorMessage={getErrorMessage("bill_to_name")}
              />

              {isEditing ? (
                <InvoiceField
                  label="Bill to Address"
                  placeholder="Enter bill to address"
                  value={values.bill_to_address}
                  onChange={handleInputChange}
                  name={"bill_to_address"}
                  type={"textarea"}
                  validation={getValidationStatus("bill_to_address", true)}
                  errorMessage={getErrorMessage("bill_to_address")}
                />
              ) : null}

              {isEditing ? (
                <InvoiceField
                  label="Ship to Address"
                  placeholder="Enter ship to address"
                  value={values.ship_to_address}
                  onChange={handleInputChange}
                  name={"ship_to_address"}
                  type={"textarea"}
                  validation={getValidationStatus("ship_to_address", true)}
                  errorMessage={getErrorMessage("ship_to_address")}
                />
              ) : null}

              {isEditing ? (
                <InvoiceField
                  label="HSN/SAC"
                  placeholder="Enter HSN/SAC"
                  value={
                    Array.isArray(values.hsn_sac_codes)
                      ? values.hsn_sac_codes.join(",")
                      : values.hsn_sac_codes
                  }
                  onChange={handleInputChange}
                  name={"hsn_sac_codes"}
                  validation={getValidationStatus("hsn_code", true)}
                  // required
                  onBlur={validateOnBlur}
                  errorMessage={getErrorMessage("hsn_code")}
                />
              ) : null}

              <InvoiceField
                label="Invoice"
                required
                customEle={
                  values.invoice_image && values.invoice_image.file_name ? (
                    <Box
                      display={"flex"}
                      direction={"row"}
                      gap={2}
                      alignItems={"center"}
                    >
                      {values.invoice_image.pre_signed_url ? (
                        <a
                          href={values.invoice_image.pre_signed_url}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.invoiceLink}
                        >
                          <span>
                            {values.invoice_image.file_name || "Invoice Link"}
                          </span>
                          <Download style={{ fontSize: "16px" }} />
                        </a>
                      ) : (
                        <span>{values.invoice_image.file_name}</span>
                      )}

                      <IconButton onClick={clearInvoiceImage}>
                        <CancelOutlined />
                      </IconButton>
                    </Box>
                  ) : (
                    <UploadFile
                      label={"Upload invoice"}
                      onUpload={(file) => {
                        // console.log(file);
                        setValues((prev) => ({
                          ...prev,
                          invoice_image: { file_name: file.name },
                        }));
                        setEditedImage(file);
                      }}
                    />
                  )
                }
              />

              <InvoiceField
                label="Additional Charges"
                placeholder="Enter additional charges"
                helperText="Transportation, Packaging, Additional Work, etc"
                value={values.additional_charges}
                onChange={handleInputChange}
                name={"additional_charges"}
                type={"number"}
              />

              <InvoiceField
                label={"IRN Invoice"}
                customEle={
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="irn_invoice"
                      onChange={handleInputChange}
                      value={values.irn_invoice}
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                }
              />

              <InvoiceField
                label={"GRN Number"}
                customEle={
                  <Autocomplete
                    disablePortal
                    disableClearable
                    value={{ grn_id: values.grn_id || "" }}
                    getOptionLabel={(x) => x?.grn_id?.toString()}
                    sx={{ width: "360px" }}
                    onChange={(event, newValue) => {
                      const { grn_id, grn_value, grn_date } = newValue;
                      setValues((prev) => ({
                        ...prev,
                        grn_id,
                        grn_value,
                        grn_date: convertDate(grn_date, true),
                      }));
                    }}
                    options={grnData || []}
                    renderInput={(params) => (
                      <TextField {...params} label="Enter GRN Number" />
                    )}
                  />
                }
              />

              <InvoiceField
                label="GRN date (YYYY-MM-DD)"
                placeholder=" GRN date"
                value={values.grn_date}
                onChange={handleInputChange}
                name={"grn_date"}
                disabled={true}
              />

              <InvoiceField
                label="GRN Value"
                placeholder=" GRN Value"
                value={values.grn_value ? convertToINR(values.grn_value) : ""}
                onChange={handleInputChange}
                name={"grn_value"}
                disabled={true}
              />
            </Box>
          </Grid>
        </Grid>
        <Box
          boxShadow={"10px 8px 4px 10px rgba(0, 0, 0, 0.25)"}
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          columnGap={"12px"}
          zIndex={1}
          sx={{ bgcolor: "white", padding: "20px 40px" }}
          position={"fixed"}
          bottom={0}
          right={0}
          left={0}
        >
          <MuiButton
            variant="contained"
            sx={{ backgroundColor: COLOR_CONSTANTS["on-surface/secondary"] }}
            startIcon={<Close />}
            onClick={() => navigate("/dashboard/invoice-management/")}
          >
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            startIcon={<Send />}
            sx={{ backgroundColor: COLOR_CONSTANTS["primary/default"] }}
            disabled={isSaveDisabled()}
            onClick={async () => {
              const {
                invoice_image,
                system_validation_status,
                hsn_sac_codes,
                ...data
              } = values;
              // console.log(values);
              const payload = {
                ...data,
                invoice_value: coerceNumericPayload(data.invoice_value),
                additional_charges: coerceNumericPayload(data.additional_charges),
                po_code: location.state.po_code,
                hsn_sac_codes: hsn_sac_codes
                  ? Array.isArray(hsn_sac_codes)
                    ? hsn_sac_codes
                    : hsn_sac_codes.split(",")
                  : [],
                // If system validation status is 2(auto rejected), then set it to 3(send for approval)
                system_validation_status:
                  system_validation_status === 2 ? 3 : system_validation_status,
              };
              if (location.state.edit) {
                // Include invoice id at top level so backend can look up by id when invoice_no changes
                if (location.state.id) {
                  payload.id = location.state.id;
                }
                // Call PUT method with updated values
                await updateInvoiceMutate({ payload });
              } else {
                await addInvoiceMutate({ payload });
              }
            }}
          >
            Submit
          </MuiButton>
        </Box>
      </Box>
    </div>
  );
};

export default AddEditInvoice;
